import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { APIErrorResponse } from "src/types/generic";
import apiClient from "../apiClient";

export type {
  SalaryStatus,
  SalaryListRow,
  SalaryPaymentRow,
  SalaryAttachmentRow,
  SalaryDetail,
} from "src/types/salary";

export const salarySalariesKeys = {
  list: (
    page: number,
    search: string,
    status: string,
    branchId: number,
    salaryMonth: string,
    paymentDateFrom: string,
    paymentDateTo: string,
  ) =>
    [
      "salary-salaries",
      page,
      search,
      status,
      branchId,
      salaryMonth,
      paymentDateFrom,
      paymentDateTo,
    ] as const,
  detail: (id: number) => ["salary-salary", id] as const,
  attachments: (id: number) => ["salary-salary-attachments", id] as const,
};

export interface IGetSalarySalariesParams {
  page?: number;
  search?: string;
  status?: "" | import("src/types/salary").SalaryStatus;
  branch_id?: number;
  salary_month?: string;
  payment_date_from?: string;
  payment_date_to?: string;
}

export const useGetSalarySalaries = (params: IGetSalarySalariesParams) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: salarySalariesKeys.list(
      params.page ?? 1,
      params.search ?? "",
      params.status ?? "",
      params.branch_id ?? 0,
      params.salary_month ?? "",
      params.payment_date_from ?? "",
      params.payment_date_to ?? "",
    ),
    queryFn: async () => {
      return apiClient.get(`/api/salaries`, {
        params: {
          page: params.page ?? 1,
          search: params.search?.trim() || undefined,
          status: params.status || undefined,
          branch_id: params.branch_id || undefined,
          salary_month: params.salary_month || undefined,
          payment_date_from: params.payment_date_from || undefined,
          payment_date_to: params.payment_date_to || undefined,
        },
      });
    },
  });

export const useGetSalarySalary = (id: number | null, enabled = true) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: salarySalariesKeys.detail(id ?? 0),
    queryFn: async () => {
      return apiClient.get(`/api/salaries/${id}`);
    },
    enabled: enabled && !!id && id > 0,
  });

export type SalaryPayload = {
  branch_id: number;
  employee_id: number;
  type?: string | null;
  additional_pay?: number;
  deduction?: number;
  // An empty array clears any saved deductions; omitting the key leaves them as-is.
  event_deductions?: Array<{
    purchase_event_id: number;
    amount: number;
  }>;
  payment_date?: string | null;
  received_date?: string | null;
  remind_me_at?: string | null;
  note?: string | null;
  payments?: Array<{
    payment_method_id: number;
    amount: number;
    date?: string;
    note?: string | null;
  }>;
};

export const useCreateSalarySalary = () => {
  const queryClient = useQueryClient();
  return useMutation<any, APIErrorResponse, SalaryPayload>({
    mutationFn: (data) => apiClient.post(`/api/salaries`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["salary-salaries"] });
    },
  });
};

export const useUpdateSalarySalary = () => {
  const queryClient = useQueryClient();
  return useMutation<any, APIErrorResponse, { id: number } & Partial<SalaryPayload>>({
    mutationFn: ({ id, ...data }) => apiClient.put(`/api/salaries/${id}`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["salary-salaries"] });
      queryClient.invalidateQueries({ queryKey: salarySalariesKeys.detail(variables.id) });
    },
  });
};

export const useDeleteSalarySalary = () => {
  const queryClient = useQueryClient();
  return useMutation<any, APIErrorResponse, number>({
    mutationFn: (id) => apiClient.delete(`/api/salaries/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["salary-salaries"] });
    },
  });
};

export const useDownloadSalarySalaries = (params: IGetSalarySalariesParams) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: ["download-salary-salaries"],
    queryFn: async () => {
      return apiClient.get(`/api/salaries/download`, { params });
    },
    enabled: false,
    gcTime: 0,
  });

export const usePaySalarySalary = () => {
  const queryClient = useQueryClient();
  return useMutation<
    any,
    APIErrorResponse,
    { salaryId: number; payment_method_id: number; amount: number; date?: string; note?: string }
  >({
    mutationFn: ({ salaryId, ...data }) => apiClient.post(`/api/salaryPayments`, { salary_id: salaryId, ...data }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["salary-salaries"] });
      queryClient.invalidateQueries({ queryKey: salarySalariesKeys.detail(variables.salaryId) });
    },
  });
};

export const useGetSalarySalaryAttachments = (salaryId: number | null, enabled = false) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: salarySalariesKeys.attachments(salaryId ?? 0),
    queryFn: async () => {
      return apiClient.get(`/api/salaryFiles`, {
        params: { salary_id: salaryId },
      });
    },
    enabled: enabled && !!salaryId && salaryId > 0,
  });

export const useAddSalarySalaryAttachment = () => {
  const queryClient = useQueryClient();
  return useMutation<any, APIErrorResponse, { salaryId: number; file: File }>({
    mutationFn: ({ salaryId, file }) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("salary_id", String(salaryId));
      return apiClient.post(`/api/salaryFiles`, formData);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: salarySalariesKeys.attachments(variables.salaryId) });
      queryClient.invalidateQueries({ queryKey: salarySalariesKeys.detail(variables.salaryId) });
    },
  });
};

export const useDeleteSalarySalaryAttachment = () => {
  const queryClient = useQueryClient();
  return useMutation<any, APIErrorResponse, { salaryId: number; salaryFileId: number }>({
    mutationFn: ({ salaryFileId }) =>
      apiClient.delete(`/api/salaryFiles/${salaryFileId}`),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: salarySalariesKeys.attachments(variables.salaryId) });
      queryClient.invalidateQueries({ queryKey: salarySalariesKeys.detail(variables.salaryId) });
    },
  });
};
